import { useState, useEffect } from 'react';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { useMatches } from './useMatches';
import { User } from '../types/user.types';

interface EnrichedMatch {
  id: string;
  matchId: string;
  user: User;
  matchedAt: Timestamp;
  lastMessageAt?: Timestamp;
  sharedCity: string;
  sharedLifestyle: string[];
  unreadCount?: { [userId: string]: number };
  compatibilityScore?: number;
}

export const useEnrichedMatches = () => {
  const { currentUser } = useAuth();
  const {
    matches,
    loading: matchesLoading,
    error: matchesError,
    indexBuilding,
  } = useMatches();
  const [enrichedMatches, setEnrichedMatches] = useState<EnrichedMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [enrichError, setEnrichError] = useState<string | null>(null);

  useEffect(() => {
    if (matchesLoading) return;
    
    const fetchMatchedUsers = async () => {
      if (!currentUser || !matches.length) {
        setEnrichedMatches([]);
        setEnrichError(null);
        setLoading(false);
        return;
      }

      try {
        setEnrichError(null);
        const enriched = await Promise.all(
          matches.map(async (match) => {
            const matchedUserId = match.users.find(uid => uid !== currentUser.uid);
            if (!matchedUserId) return null;

            const userDoc = await getDoc(doc(firestore, 'users', matchedUserId));
            if (!userDoc.exists()) return null;

            const userData = { id: userDoc.id, ...userDoc.data() } as User;

            return {
              id: match.id,
              matchId: match.id,
              user: userData,
              matchedAt: match.matchedAt,
              lastMessageAt: match.lastMessageAt,
              sharedCity: match.sharedCity,
              sharedLifestyle: match.sharedLifestyle,
              unreadCount: match.unreadCount,
              compatibilityScore: match.compatibilityScore,
            };
          })
        );

        setEnrichedMatches(enriched.filter(Boolean) as EnrichedMatch[]);
      } catch (error) {
        console.error('Error fetching matched users:', error);
        setEnrichError('FAILED');
      } finally {
        setLoading(false);
      }
    };

    fetchMatchedUsers();
  }, [matches, matchesLoading, currentUser]);

  // Surface the underlying matches-query error first (index/permission), then
  // any enrichment failure, so the UI can explain why matches aren't showing.
  const error = matchesError ?? enrichError;

  return { enrichedMatches, loading, error, indexBuilding };
};
